import { useEffect, useMemo, useState } from 'react';
import { differenceInMinutes } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { ChevronRight, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import type {
  AgendaAppointment,
  AgendaProfessional,
  AppointmentStatus,
} from '@agenda-facil/contracts';
import { Avatar, Surface, cn } from '@agenda-facil/ui';
import { appointmentStatusClass, appointmentStatusLabel } from './appointment-status';

const START_MINUTE = 8 * 60;
const END_MINUTE = 20 * 60;
const SLOT_MINUTES = 30;
const SLOT_HEIGHT = 44;
const MINUTE_HEIGHT = SLOT_HEIGHT / SLOT_MINUTES;
const GUTTER_WIDTH = 68;
const PROFESSIONAL_WIDTH = 260;

const timelineSlots = Array.from(
  { length: (END_MINUTE - START_MINUTE) / SLOT_MINUTES },
  (_, index) => START_MINUTE + index * SLOT_MINUTES,
);

const hourMarks = timelineSlots.filter((minute) => minute % 60 === 0);
const timelineHeight = timelineSlots.length * SLOT_HEIGHT;

function toClock(minute: number): string {
  return `${Math.floor(minute / 60)
    .toString()
    .padStart(2, '0')}:${(minute % 60).toString().padStart(2, '0')}`;
}

function clockToMinute(clock: string): number {
  const [hour = 0, minute = 0] = clock.split(':').map(Number);
  return hour * 60 + minute;
}

export function getTimelinePosition(startsAt: string, endsAt: string, timezone: string) {
  const startMinute = clockToMinute(formatInTimeZone(startsAt, timezone, 'HH:mm'));
  const durationMinutes = Math.max(15, differenceInMinutes(new Date(endsAt), new Date(startsAt)));
  const visibleStart = Math.max(startMinute, START_MINUTE);
  const visibleEnd = Math.min(startMinute + durationMinutes, END_MINUTE);
  const top = (visibleStart - START_MINUTE) * MINUTE_HEIGHT;
  const height = Math.max(28, (visibleEnd - visibleStart) * MINUTE_HEIGHT);
  return { top, height, durationMinutes, startMinute };
}

function overlapsSlot(
  appointment: AgendaAppointment,
  slotMinute: number,
  timezone: string,
): boolean {
  const position = getTimelinePosition(appointment.startsAt, appointment.endsAt, timezone);
  return (
    position.startMinute < slotMinute + SLOT_MINUTES &&
    position.startMinute + position.durationMinutes > slotMinute
  );
}

function bookingUrl(path: string, date: string, barberId: string): string {
  const query = new URLSearchParams({ date, barberId });
  return `${path}?${query.toString()}`;
}

export interface AgendaTimelineProps {
  appointments: AgendaAppointment[];
  professionals: AgendaProfessional[];
  timezone: string;
  date: string;
  bookingPath?: string;
  select: (appointment: AgendaAppointment) => void;
}

export function AgendaTimeline({
  appointments,
  professionals,
  timezone,
  date,
  bookingPath,
  select,
}: AgendaTimelineProps) {
  const [statusFilter, setStatusFilter] = useState<AppointmentStatus | 'ALL'>('ALL');
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const statusCounts = useMemo(() => {
    const counts = Object.fromEntries(
      (Object.keys(appointmentStatusLabel) as AppointmentStatus[]).map((status) => [status, 0]),
    ) as Record<AppointmentStatus, number>;
    for (const appointment of appointments) counts[appointment.status] += 1;
    return counts;
  }, [appointments]);

  const filteredAppointments = useMemo(
    () =>
      statusFilter === 'ALL'
        ? appointments
        : appointments.filter((appointment) => appointment.status === statusFilter),
    [appointments, statusFilter],
  );

  const appointmentsByProfessional = useMemo(() => {
    const grouped = new Map<string, AgendaAppointment[]>();
    for (const professional of professionals) grouped.set(professional.id, []);
    for (const appointment of filteredAppointments) {
      grouped.get(appointment.barberId)?.push(appointment);
    }
    for (const items of grouped.values()) {
      items.sort((left, right) => left.startsAt.localeCompare(right.startsAt));
    }
    return grouped;
  }, [filteredAppointments, professionals]);

  const allAppointmentsByProfessional = useMemo(() => {
    const grouped = new Map<string, AgendaAppointment[]>();
    for (const professional of professionals) grouped.set(professional.id, []);
    for (const appointment of appointments) grouped.get(appointment.barberId)?.push(appointment);
    return grouped;
  }, [appointments, professionals]);

  const today = formatInTimeZone(now, timezone, 'yyyy-MM-dd');
  const currentMinute = clockToMinute(formatInTimeZone(now, timezone, 'HH:mm'));
  const showCurrentTime =
    date === today && currentMinute >= START_MINUTE && currentMinute <= END_MINUTE;
  const currentLineTop = (currentMinute - START_MINUTE) * MINUTE_HEIGHT;
  const gridTemplateColumns = `${GUTTER_WIDTH}px repeat(${professionals.length}, minmax(${PROFESSIONAL_WIDTH}px, 1fr))`;
  const minimumWidth = GUTTER_WIDTH + professionals.length * PROFESSIONAL_WIDTH;

  return (
    <div className="space-y-4 motion-fade-in">
      <div
        role="group"
        className="flex items-center gap-2 overflow-x-auto pb-1"
        aria-label="Filtrar agenda por status"
      >
        <button
          type="button"
          aria-pressed={statusFilter === 'ALL'}
          onClick={() => setStatusFilter('ALL')}
          className={cn(
            'min-h-11 shrink-0 rounded-xl border px-3 text-xs font-extrabold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]',
            statusFilter === 'ALL'
              ? 'border-[var(--brand)] bg-[var(--brand)] text-[var(--brand-foreground)]'
              : 'border-[var(--border)] bg-[var(--surface)] text-[var(--muted-foreground)] hover:bg-[var(--surface-raised)] hover:text-[var(--foreground)]',
          )}
        >
          Todos · {appointments.length}
        </button>
        {(Object.keys(appointmentStatusLabel) as AppointmentStatus[]).map((status) => {
          const count = statusCounts[status];
          if (count === 0 && statusFilter !== status) return null;
          return (
            <button
              key={status}
              type="button"
              aria-pressed={statusFilter === status}
              onClick={() => setStatusFilter((current) => (current === status ? 'ALL' : status))}
              className={cn(
                'min-h-11 shrink-0 rounded-xl border px-3 text-xs font-extrabold transition-[box-shadow,opacity] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]',
                appointmentStatusClass[status],
                statusFilter === status &&
                  'ring-2 ring-[var(--ring)] ring-offset-2 ring-offset-[var(--background)]',
              )}
            >
              {appointmentStatusLabel[status]} · {count}
            </button>
          );
        })}
      </div>

      <p className="flex items-center gap-1 text-xs font-bold text-[var(--muted-foreground)] md:hidden">
        Deslize para acompanhar toda a equipe <ChevronRight size={14} aria-hidden="true" />
      </p>

      <Surface className="overflow-hidden p-0">
        <div
          className="max-h-[min(72vh,820px)] min-h-[520px] snap-x snap-mandatory overflow-auto"
          style={{ scrollPaddingInlineStart: GUTTER_WIDTH }}
        >
          <div style={{ minWidth: minimumWidth, width: `max(100%, ${minimumWidth}px)` }}>
            <div
              className="sticky top-0 z-30 grid min-h-18 border-b border-[var(--border)] bg-[var(--surface)] shadow-[0_8px_20px_color-mix(in_srgb,var(--foreground)_4%,transparent)]"
              style={{ gridTemplateColumns }}
            >
              <div className="sticky left-0 z-40 flex items-center gap-1.5 bg-[var(--surface)] px-3 text-[10px] font-black uppercase tracking-[0.1em] text-[var(--muted-foreground)]">
                <Clock size={14} className="text-[var(--accent)]" aria-hidden="true" /> Hora
              </div>
              {professionals.map((professional) => {
                const count = appointmentsByProfessional.get(professional.id)?.length ?? 0;
                return (
                  <div
                    key={professional.id}
                    className="flex snap-start items-center gap-2.5 border-l border-[var(--border)] px-3"
                  >
                    <Avatar name={professional.name} size="sm" />
                    <div className="min-w-0">
                      <p className="truncate text-xs font-black text-[var(--foreground)]">
                        {professional.name}
                      </p>
                      <p className="text-[10px] font-bold text-[var(--muted-foreground)]">
                        {count === 0
                          ? 'Sem atendimentos'
                          : `${count} atendimento${count === 1 ? '' : 's'}`}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="relative grid" style={{ gridTemplateColumns, height: timelineHeight }}>
              <div className="sticky left-0 z-20 border-r border-[var(--border)] bg-[var(--surface)]">
                {hourMarks.map((minute) => (
                  <span
                    key={minute}
                    className="absolute left-3 -translate-y-1/2 text-[10px] font-black tabular-nums text-[var(--muted-foreground)]"
                    style={{ top: (minute - START_MINUTE) * MINUTE_HEIGHT }}
                  >
                    {toClock(minute)}
                  </span>
                ))}
                <span className="absolute bottom-0 left-3 translate-y-1/2 text-[10px] font-black tabular-nums text-[var(--muted-foreground)]">
                  {toClock(END_MINUTE)}
                </span>
              </div>

              {professionals.map((professional) => {
                const visibleItems = appointmentsByProfessional.get(professional.id) ?? [];
                const occupiedItems = (
                  allAppointmentsByProfessional.get(professional.id) ?? []
                ).filter(
                  (appointment) =>
                    appointment.status !== 'CANCELED' && appointment.status !== 'NO_SHOW',
                );
                return (
                  <div
                    key={professional.id}
                    className="relative snap-start border-r border-[var(--border)]"
                    style={{
                      backgroundImage:
                        'repeating-linear-gradient(to bottom, transparent 0, transparent 43px, var(--border) 43px, var(--border) 44px)',
                    }}
                  >
                    {timelineSlots.map((slotMinute) => {
                      const isOccupied = occupiedItems.some((appointment) =>
                        overlapsSlot(appointment, slotMinute, timezone),
                      );
                      const isPast =
                        date < today || (date === today && slotMinute <= currentMinute);
                      if (!bookingPath || isOccupied || isPast) return null;
                      const time = toClock(slotMinute);
                      return (
                        <Link
                          key={slotMinute}
                          to={bookingUrl(bookingPath, date, professional.id)}
                          aria-label={`Agendar com ${professional.name} às ${time}`}
                          className="absolute inset-x-1.5 z-0 flex items-center justify-center rounded-lg text-[10px] font-extrabold text-[var(--muted-foreground)] opacity-65 transition-[background-color,color,opacity] duration-150 hover:bg-[var(--accent-soft)] hover:text-[var(--accent)] hover:opacity-100 focus-visible:z-10 focus-visible:bg-[var(--accent-soft)] focus-visible:text-[var(--accent)] focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] md:opacity-0"
                          style={{
                            top: (slotMinute - START_MINUTE) * MINUTE_HEIGHT,
                            height: SLOT_HEIGHT,
                          }}
                        >
                          + Agendar {time}
                        </Link>
                      );
                    })}

                    {visibleItems.map((appointment) => {
                      const position = getTimelinePosition(
                        appointment.startsAt,
                        appointment.endsAt,
                        timezone,
                      );
                      if (
                        position.startMinute >= END_MINUTE ||
                        position.startMinute + position.durationMinutes <= START_MINUTE
                      )
                        return null;
                      const compact = position.durationMinutes < 45;
                      const startsAt = formatInTimeZone(appointment.startsAt, timezone, 'HH:mm');
                      return (
                        <button
                          key={appointment.id}
                          type="button"
                          onClick={() => select(appointment)}
                          aria-label={`${startsAt}, ${appointment.customerName}, ${appointment.serviceName}, ${appointmentStatusLabel[appointment.status]}`}
                          className={cn(
                            'timeline-block absolute inset-x-1.5 z-10 overflow-hidden rounded-xl border px-2.5 py-2 text-left shadow-[var(--shadow-sm)] focus-visible:z-20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--surface)]',
                            appointmentStatusClass[appointment.status],
                          )}
                          style={{
                            top: position.top + 2,
                            height: Math.max(26, position.height - 4),
                          }}
                        >
                          <div className="flex items-center justify-between gap-2 text-[10px] font-black tabular-nums">
                            <span>{startsAt}</span>
                            {!compact && (
                              <span className="truncate">
                                {appointmentStatusLabel[appointment.status]}
                              </span>
                            )}
                          </div>
                          <p className="mt-0.5 truncate text-xs font-black text-[var(--foreground)]">
                            {appointment.customerName}
                          </p>
                          {!compact && (
                            <p className="mt-0.5 truncate text-[10px] font-bold opacity-80">
                              {appointment.serviceName} · {position.durationMinutes} min
                            </p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}

              {showCurrentTime && (
                <div
                  className="pointer-events-none absolute inset-x-0 z-20 flex items-center"
                  style={{ top: currentLineTop }}
                  role="img"
                  aria-label={`Horário atual: ${formatInTimeZone(now, timezone, 'HH:mm')}`}
                >
                  <span className="relative z-10 ml-1 rounded-md bg-[var(--accent)] px-1.5 py-0.5 text-[9px] font-black tabular-nums text-[var(--brand-foreground)]">
                    {formatInTimeZone(now, timezone, 'HH:mm')}
                  </span>
                  <span className="h-px flex-1 bg-[var(--accent)] shadow-[0_0_0_1px_color-mix(in_srgb,var(--accent)_20%,transparent)]" />
                </div>
              )}
            </div>
          </div>
        </div>
      </Surface>
    </div>
  );
}
